import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ChevronRight, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getFixtureSlotOccupancy } from '@/api/tournament/tournamentApi';
import {
  ForcedReason,
  type Fixture,
  type SlotOccupancy,
} from '@/types/tournament.model';
import { formatDateForUser, formatTimeForUser } from '@/utils/formatters';
import { matchCount } from '@/utils/fixture-utils';
import { FORCED_REASON_STYLES } from './ForcedReasonBadge';
import { SlotOccupancyContext } from './slot-occupancy-context';

interface SlotOccupancyProviderProps {
  tournamentId: string;
  /** Cache key: a new fixture object means the times changed and the slots must be re-fetched. */
  fixture: Fixture | null;
  children: React.ReactNode;
}

type OpenedBadge = { matchId: string; reason: ForcedReason };

/**
 * Holds the slot detail the organizer sees when tapping a forced-match badge, and the request that
 * feeds it. The request goes out on the first tap and is reused for the rest of the badges: with
 * several tournaments sharing a block the organizer opens one badge after another, and they all
 * read the same answer.
 */
export function SlotOccupancyProvider({
  tournamentId,
  fixture,
  children,
}: SlotOccupancyProviderProps) {
  const navigate = useNavigate();
  const [opened, setOpened] = useState<OpenedBadge | null>(null);
  const [slots, setSlots] = useState<SlotOccupancy[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);

  // Rescheduling or deleting a match reloads the fixture, and the cached slots were computed
  // against the previous times: drop them so the next tap asks again.
  useEffect(() => {
    setSlots(null);
    setHasFailed(false);
  }, [fixture]);

  const openFor = useCallback(
    async (matchId: string, reason: ForcedReason) => {
      setOpened({ matchId, reason });
      if (slots) return;
      setIsLoading(true);
      setHasFailed(false);
      try {
        setSlots(await getFixtureSlotOccupancy(tournamentId));
      } catch (error) {
        console.error('Error loading slot occupancy:', error);
        setHasFailed(true);
      } finally {
        setIsLoading(false);
      }
    },
    [tournamentId, slots],
  );

  const slot = useMemo(() => {
    if (!opened || !slots) return null;
    return (
      slots.find((candidate) =>
        candidate.occupants.some(
          (occupant) => occupant.matchId === opened.matchId,
        ),
      ) ?? null
    );
  }, [opened, slots]);

  const styles = opened ? FORCED_REASON_STYLES[opened.reason] : null;

  return (
    <SlotOccupancyContext.Provider value={openFor}>
      {children}
      <Dialog
        open={!!opened}
        onOpenChange={(next) => {
          if (!next) setOpened(null);
        }}
      >
        {/* Same shape as CategoryRankingPointsInfoModal: a centred card, capped so a six-line
            list does not stretch across a monitor. */}
        <DialogContent className="mx-auto max-h-[85vh] overflow-y-auto p-6 sm:max-w-[520px]">
          {opened && styles && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-base">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  {styles.label}
                </DialogTitle>
                <DialogDescription>{styles.title}</DialogDescription>
              </DialogHeader>

              <div>
                {isLoading && (
                  <p className="flex items-center gap-2 text-sm text-gray-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Buscando qué más hay a esa hora...
                  </p>
                )}

                {!isLoading && hasFailed && (
                  <p className="text-sm text-gray-500">
                    No pudimos traer el detalle de la franja. Probá de nuevo en
                    un rato.
                  </p>
                )}

                {!isLoading && !hasFailed && !slot && (
                  <p className="text-sm text-gray-500">
                    Este partido no cae en ninguna franja del club, así que no
                    hay nada con qué compararlo.
                  </p>
                )}

                {!isLoading && !hasFailed && slot && (
                  <>
                    <p className="text-sm font-medium text-gray-900">
                      {formatDateForUser(slot.startDate)} ·{' '}
                      {formatTimeForUser(slot.startDate)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {slot.courtsQuantity == null
                        ? `${matchCount(slot.occupants.length)} en esta franja`
                        : `${matchCount(slot.occupants.length)} y ${
                            slot.courtsQuantity === 1
                              ? '1 cancha'
                              : `${slot.courtsQuantity} canchas`
                          }`}
                    </p>

                    <ul className="mt-3 space-y-1.5">
                      {slot.occupants.map((occupant) => (
                        <SlotOccupantRow
                          key={occupant.matchId}
                          tournamentName={occupant.tournamentName}
                          stage={occupant.stage}
                          isTappedMatch={occupant.matchId === opened.matchId}
                          onOpenTournament={
                            occupant.currentTournament
                              ? undefined
                              : () =>
                                  navigate(`/torneos/${occupant.tournamentId}`)
                          }
                        />
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </SlotOccupancyContext.Provider>
  );
}

function SlotOccupantRow({
  tournamentName,
  stage,
  isTappedMatch,
  onOpenTournament,
}: {
  tournamentName: string;
  stage: string;
  isTappedMatch: boolean;
  onOpenTournament?: () => void;
}) {
  const content = (
    <>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium text-gray-900">
          {tournamentName}
        </span>
        <span className="block text-xs text-gray-500">{stage}</span>
      </span>
      {isTappedMatch && (
        <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600">
          Este partido
        </span>
      )}
      {onOpenTournament && (
        <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
      )}
    </>
  );

  // Only matches of other tournaments are worth a tap: they are the ones the organizer cannot
  // move from here, and their fixture is a different page.
  return (
    <li>
      {onOpenTournament ? (
        <button
          type="button"
          onClick={onOpenTournament}
          className="flex w-full items-center gap-2 rounded-md border border-gray-200 p-2 text-left text-sm transition-colors hover:bg-gray-50"
        >
          {content}
        </button>
      ) : (
        <div className="flex w-full items-center gap-2 rounded-md border border-gray-200 p-2 text-sm">
          {content}
        </div>
      )}
    </li>
  );
}
